import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';

import {
  PlatformSessionRequestNotice,
  usePlatformSessionRequests,
} from '@/components/platform-session-request-notice';
import { FormField } from '@/components/shared/form-field';
import { ReadOnlyNotice } from '@/components/shared/read-only-notice';
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Listbox } from '@/components/ui/listbox';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { useIsPlatformAdmin } from '@/hooks/use-is-platform-admin';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import {
  formatTicketNumber,
  SUPPORT_TICKET_PRIORITIES,
  SUPPORT_TICKET_STATUSES,
  type SupportTicketEvent,
  type SupportTicketMessage,
  type SupportTicketPriority,
  type SupportTicketStatus,
  type SupportTicketSummary,
  type SupportTicketTimeline,
  isFirstReplyOverdue,
  isResolutionOverdue,
} from '@/lib/support-ticket-types';
import { cn } from '@/utils/cn';
import { formatDateTime } from '@/utils/format';

interface PlatformStaffOption {
  id: string;
  label: string;
}

const statusVariant = (status: SupportTicketSummary['status']): BadgeVariant => {
  if (status === 'escalated') return 'danger';
  if (status === 'awaiting_platform') return 'warning';
  if (status === 'resolved') return 'success';
  if (status === 'closed') return 'neutral';
  return 'info';
};

type TimelineItem =
  | { kind: 'message'; at: string; id: string; message: SupportTicketMessage }
  | { kind: 'event'; at: string; id: string; event: SupportTicketEvent };

const initialsOf = (name: string): string => {
  const letters = name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .filter(Boolean)
    .join('');
  return letters || '?';
};

const readField = (value: Record<string, unknown> | null, key: string): string | null => {
  const raw = value?.[key];
  return typeof raw === 'string' ? raw : null;
};

const isTicketStatus = (value: string): value is SupportTicketStatus =>
  (SUPPORT_TICKET_STATUSES as readonly string[]).includes(value);

const isTicketPriority = (value: string): value is SupportTicketPriority =>
  (SUPPORT_TICKET_PRIORITIES as readonly string[]).includes(value);

const ThreadSpeakerMark = ({
  name,
  tone,
}: {
  name: string;
  tone: 'church' | 'unity' | 'internal';
}) => (
  <span
    aria-hidden
    className={cn(
      'mb-0.5 flex size-8 shrink-0 items-center justify-center rounded-full text-caption font-semibold',
      tone === 'unity' && 'bg-brand text-on-brand',
      tone === 'church' && 'border border-border bg-surface text-fg',
      tone === 'internal' && 'bg-warning-subtle text-warning-fg',
    )}
  >
    {initialsOf(name)}
  </span>
);

const threadSpeaker = (
  message: SupportTicketMessage,
  t: (key: string) => string,
): {
  sideLabel: string;
  tone: 'church' | 'unity' | 'internal';
  bubbleClass: string;
  sideClass: string;
  alignRight: boolean;
} => {
  if (message.isInternal) {
    return {
      sideLabel: t('supportTickets.detail.internalNote'),
      tone: 'internal',
      bubbleClass: 'border border-dashed border-warning/50 bg-warning-subtle',
      sideClass: 'text-warning-fg',
      alignRight: true,
    };
  }
  if (message.authorSide === 'platform') {
    return {
      sideLabel: t('supportTickets.detail.sidePlatform'),
      tone: 'unity',
      bubbleClass: 'bg-brand/10',
      sideClass: 'text-brand',
      alignRight: true,
    };
  }
  return {
    sideLabel: t('supportTickets.detail.sideChurch'),
    tone: 'church',
    bubbleClass: 'border border-border bg-surface',
    sideClass: 'text-fg-secondary',
    alignRight: false,
  };
};

const ThreadMessage = ({ message }: { message: SupportTicketMessage }) => {
  const { t } = useTranslation('admin');
  const name = message.authorName ?? t('audit.emptyValue');
  const speaker = threadSpeaker(message, t);

  return (
    <li className={cn('flex items-end gap-2', speaker.alignRight ? 'flex-row-reverse' : 'flex-row')}>
      <ThreadSpeakerMark name={name} tone={speaker.tone} />
      <div className={cn('w-fit max-w-[min(100%,28rem)] rounded-lg px-3 py-2', speaker.bubbleClass)}>
        <div className="mb-1 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="text-body-sm font-semibold text-fg">{name}</span>
          <span className={cn('text-caption font-medium', speaker.sideClass)}>
            {speaker.sideLabel}
          </span>
          <time dateTime={message.createdAt} className="text-caption text-fg-tertiary">
            {formatDateTime(message.createdAt)}
          </time>
        </div>
        <p className="whitespace-pre-wrap break-words text-body text-fg">{message.body}</p>
        {message.attachments.length > 0 ? (
          <ul className="mt-2 space-y-1">
            {message.attachments.map((file) => (
              <li key={file.id}>
                {file.downloadUrl ? (
                  <a
                    href={file.downloadUrl}
                    className="text-body-sm text-brand underline-offset-2 hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {file.fileName}
                  </a>
                ) : (
                  <span className="text-body-sm text-fg-secondary">{file.fileName}</span>
                )}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </li>
  );
};

export const PlatformSupportTicketDetailDialog = ({
  ticketId,
  open,
  onOpenChange,
}: {
  ticketId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { t } = useTranslation('admin');
  const { t: tErrors } = useTranslation('errors');
  const isAdmin = useIsPlatformAdmin();
  const queryClient = useQueryClient();

  const [replyBody, setReplyBody] = useState('');
  const [replyInternal, setReplyInternal] = useState(false);
  const [escalateOpen, setEscalateOpen] = useState(false);
  const [escalateReason, setEscalateReason] = useState('');
  const [visitOpen, setVisitOpen] = useState(false);
  const [visitReason, setVisitReason] = useState('');
  const [visitLink, setVisitLink] = useState<string | null>(null);

  const timeline = useQuery({
    queryKey: ['platform-support-ticket-timeline', ticketId],
    queryFn: () => api.get<SupportTicketTimeline>(`/platform/support/tickets/${ticketId}/timeline`),
    enabled: open && Boolean(ticketId),
  });
  const sessionRequests = usePlatformSessionRequests({
    ticketId: ticketId ?? undefined,
    enabled: open && Boolean(ticketId),
  });
  const currentSession = sessionRequests.data?.data[0];

  const staff = useQuery({
    queryKey: ['platform-support-staff'],
    queryFn: async () => {
      const result = await api.get<{
        data: Array<{
          id: string;
          firstName: string;
          lastName: string;
          platformRole: string;
          status: string;
        }>;
      }>('/platform/users', {
        query: {
          page: 1,
          pageSize: 100,
          platformRole: 'platform_admin,platform_manager',
          status: 'active',
          sort: 'lastName',
        },
      });
      return result.data
        .filter((row) => row.platformRole !== 'none')
        .map((row) => ({
          id: row.id,
          label: `${row.firstName} ${row.lastName}`.trim(),
        }));
    },
    enabled: open,
  });

  const ticket = timeline.data?.ticket;

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['platform-support-tickets'] });
    await queryClient.invalidateQueries({ queryKey: ['platform-support-ticket-timeline', ticketId] });
    await queryClient.invalidateQueries({ queryKey: ['platform-dashboard'] });
    await queryClient.invalidateQueries({ queryKey: ['platform-church-open-tickets'] });
  };

  const onError = (err: unknown) => {
    toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
  };

  const postMessage = useMutation({
    mutationFn: (body: { body: string; isInternal: boolean }) =>
      api.post(`/platform/support/tickets/${ticketId}/messages`, {
        body: body.body,
        attachments: [],
        isInternal: body.isInternal,
      }),
    onSuccess: async () => {
      toast.success(t('supportTickets.detail.replySent'));
      setReplyBody('');
      setReplyInternal(false);
      await invalidate();
    },
    onError,
  });

  const assign = useMutation({
    mutationFn: (assignedPlatformUserId: string | null) =>
      api.post(`/platform/support/tickets/${ticketId}/assign`, { assignedPlatformUserId }),
    onSuccess: async () => {
      toast.success(t('supportTickets.detail.assigned'));
      await invalidate();
    },
    onError,
  });

  const setPriorityMutation = useMutation({
    mutationFn: (next: SupportTicketSummary['priority']) =>
      api.post(`/platform/support/tickets/${ticketId}/priority`, { priority: next }),
    onSuccess: async () => {
      toast.success(t('supportTickets.detail.priorityUpdated'));
      await invalidate();
    },
    onError,
  });

  const escalate = useMutation({
    mutationFn: (reason?: string) =>
      api.post(`/platform/support/tickets/${ticketId}/escalate`, {
        ...(reason?.trim() ? { reason: reason.trim() } : {}),
      }),
    onSuccess: async () => {
      toast.success(t('supportTickets.detail.escalated'));
      setEscalateOpen(false);
      setEscalateReason('');
      await invalidate();
    },
    onError,
  });

  const resolve = useMutation({
    mutationFn: () => api.post(`/platform/support/tickets/${ticketId}/resolve`, {}),
    onSuccess: async () => {
      toast.success(t('supportTickets.detail.resolved'));
      await invalidate();
    },
    onError,
  });

  const reopen = useMutation({
    mutationFn: () => api.post(`/platform/support/tickets/${ticketId}/reopen`, {}),
    onSuccess: async () => {
      toast.success(t('supportTickets.detail.reopened'));
      await invalidate();
    },
    onError,
  });

  const close = useMutation({
    mutationFn: () => api.post(`/platform/support/tickets/${ticketId}/close`, {}),
    onSuccess: async () => {
      toast.success(t('supportTickets.detail.closed'));
      await invalidate();
    },
    onError,
  });

  const startVisit = useMutation({
    mutationFn: (reason: string) =>
      api.post(`/platform/support/tickets/${ticketId}/start-visit`, { reason }),
    onSuccess: async () => {
      setVisitOpen(false);
      toast.success(t('supportTickets.detail.visitStarted'));
      await invalidate();
      await queryClient.invalidateQueries({ queryKey: ['platform-support-session-requests'] });
    },
    onError,
  });

  const assigneeOptions = useMemo((): PlatformStaffOption[] => {
    const options = staff.data ?? [];
    if (ticket?.assignedPlatformUserId && ticket.assignedPlatformName) {
      const exists = options.some((row) => row.id === ticket.assignedPlatformUserId);
      if (!exists) {
        return [
          { id: ticket.assignedPlatformUserId, label: ticket.assignedPlatformName },
          ...options,
        ];
      }
    }
    return options;
  }, [staff.data, ticket]);

  const priorityOptions = useMemo(
    () =>
      SUPPORT_TICKET_PRIORITIES.map((value) => ({
        value,
        label: t(`supportTickets.priorities.${value}`),
      })),
    [t],
  );

  const assigneeListboxOptions = useMemo(
    () => [
      { value: '', label: t('supportTickets.detail.unassigned') },
      ...assigneeOptions.map((row) => ({ value: row.id, label: row.label })),
    ],
    [assigneeOptions, t],
  );

  const timelineItems = useMemo((): TimelineItem[] => {
    if (!timeline.data) return [];
    const messages: TimelineItem[] = timeline.data.messages.map((message) => ({
      kind: 'message',
      at: message.createdAt,
      id: message.id,
      message,
    }));
    const events: TimelineItem[] = timeline.data.events
      .filter((event) => event.kind !== 'message')
      .map((event) => ({
        kind: 'event',
        at: event.createdAt,
        id: event.id,
        event,
      }));
    return [...messages, ...events].sort(
      (a, b) => Date.parse(a.at) - Date.parse(b.at) || a.id.localeCompare(b.id),
    );
  }, [timeline.data]);

  const eventLabel = (event: SupportTicketEvent): string => {
    if (event.kind === 'status_changed') {
      const before = readField(event.beforeValue, 'status');
      const after = readField(event.afterValue, 'status');
      if (before && after && isTicketStatus(before) && isTicketStatus(after)) {
        return t('supportTickets.events.statusChangedDetail', {
          before: t(`supportTickets.statuses.${before}`),
          after: t(`supportTickets.statuses.${after}`),
        });
      }
    }
    if (event.kind === 'priority_changed') {
      const before = readField(event.beforeValue, 'priority');
      const after = readField(event.afterValue, 'priority');
      if (before && after && isTicketPriority(before) && isTicketPriority(after)) {
        return t('supportTickets.events.priorityChangedDetail', {
          before: t(`supportTickets.priorities.${before}`),
          after: t(`supportTickets.priorities.${after}`),
        });
      }
    }
    return t(`supportTickets.events.${event.kind}`, {
      defaultValue: event.kind,
      actor: event.actorName ?? t('supportTickets.detail.systemActor'),
    });
  };

  const canReply = ticket && ticket.status !== 'closed';
  const canMutate = isAdmin && ticket && ticket.status !== 'closed';
  const canResolve = canMutate && ticket.status !== 'resolved';
  const canReopen =
    isAdmin && ticket && (ticket.status === 'resolved' || ticket.status === 'closed');
  const canClose = canMutate && ticket.status !== 'closed';
  const canEscalate =
    canMutate && ticket.status !== 'resolved' && ticket.status !== 'closed' && ticket.escalationLevel < 2;
  const canStartVisit = isAdmin && ticket && ticket.status !== 'closed';

  const defaultVisitReason =
    ticket
      ? t('supportTickets.detail.visitReasonDefault', {
          number: formatTicketNumber(ticket.number),
          subject: ticket.subject,
        })
      : '';

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setReplyBody('');
      setReplyInternal(false);
      setEscalateOpen(false);
      setEscalateReason('');
      setVisitOpen(false);
      setVisitReason('');
      setVisitLink(null);
    }
    onOpenChange(next);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent closeLabel={t('common.cancel')} size="lg" className="sm:min-h-[min(44rem,82dvh)]">
          <DialogHeader>
            <div className="flex items-center justify-between gap-3">
              <DialogTitle>
                {ticket
                  ? t('supportTickets.detail.title', { number: formatTicketNumber(ticket.number) })
                  : t('supportTickets.detail.loadingTitle')}
              </DialogTitle>
              {ticket ? (
                <div className="flex shrink-0 items-center gap-2">
                  {ticket.escalationLevel > 0 ? (
                    <Badge variant="warning">{t('supportTickets.badges.escalated')}</Badge>
                  ) : null}
                  <Badge variant={statusVariant(ticket.status)}>
                    {t(`supportTickets.statuses.${ticket.status}`)}
                  </Badge>
                </div>
              ) : null}
            </div>
            {ticket ? (
              <p className="text-body font-medium text-fg">{ticket.subject}</p>
            ) : null}
            {ticket ? (
              <DialogDescription>
                {ticket.churchName ?? t('audit.emptyValue')}
                {' · '}
                {t(`supportTickets.categories.${ticket.category}`)}
                {' · '}
                {ticket.createdByName ?? t('audit.emptyValue')}
                {' · '}
                {formatDateTime(ticket.lastMessageAt)}
              </DialogDescription>
            ) : null}
          </DialogHeader>

          <DialogBody className="min-h-0 flex-1 overflow-visible" contentClassName="flex min-h-full flex-col gap-3">
            {timeline.isLoading && !ticket ? (
              <div className="flex justify-center py-12">
                <Spinner className="size-6 text-brand" />
              </div>
            ) : !ticket ? (
              <p className="text-body-sm text-danger">{t('supportTickets.detail.loadFailed')}</p>
            ) : (
              <>
                {currentSession ? <PlatformSessionRequestNotice request={currentSession} /> : null}
                {isFirstReplyOverdue(ticket) || isResolutionOverdue(ticket) || ticket.linkedImpersonationId ? (
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    {isFirstReplyOverdue(ticket) ? (
                      <Badge variant="danger">{t('supportTickets.badges.firstReplyOverdue')}</Badge>
                    ) : null}
                    {isResolutionOverdue(ticket) ? (
                      <Badge variant="danger">{t('supportTickets.badges.resolutionOverdue')}</Badge>
                    ) : null}
                    {ticket.linkedImpersonationId ? (
                      <Link
                        to="/support-access"
                        className="text-body-sm text-brand underline-offset-2 hover:underline"
                      >
                        {t('supportTickets.detail.viewVisits')}
                      </Link>
                    ) : null}
                  </div>
                ) : null}

                <ReadOnlyNotice message={t('supportTickets.detail.readOnlyNotice')} />

                {isAdmin && ticket.status !== 'closed' ? (
                  <div className="grid gap-4 sm:grid-cols-2">
                    <FormField label={t('supportTickets.columns.assignee')}>
                      {(field) => (
                        <Listbox
                          {...field}
                          popoverPortal="dialog"
                          value={ticket.assignedPlatformUserId ?? ''}
                          onChange={(value) => assign.mutate(value || null)}
                          options={assigneeListboxOptions}
                          disabled={assign.isPending}
                        />
                      )}
                    </FormField>
                    <FormField label={t('supportTickets.columns.priority')}>
                      {(field) => (
                        <Listbox
                          {...field}
                          popoverPortal="dialog"
                          value={ticket.priority}
                          onChange={(value) => {
                            if (!value) return;
                            setPriorityMutation.mutate(value);
                          }}
                          options={priorityOptions}
                          disabled={setPriorityMutation.isPending}
                        />
                      )}
                    </FormField>
                  </div>
                ) : null}

                <ul className="flex flex-col gap-3">
                  {timelineItems.map((item) => {
                    if (item.kind === 'event') {
                      return (
                        <li key={item.id} className="px-10 py-0.5 text-center">
                          <p className="text-caption text-fg-tertiary">
                            <time dateTime={item.at}>{formatDateTime(item.at)}</time>
                            <span className="mx-1.5">·</span>
                            {eventLabel(item.event)}
                          </p>
                        </li>
                      );
                    }
                    return <ThreadMessage key={item.id} message={item.message} />;
                  })}
                </ul>

                {canReply ? (
                  <div className="space-y-3">
                    <FormField label={t('supportTickets.detail.replyLabel')}>
                      {(field) => (
                        <Textarea
                          {...field}
                          value={replyBody}
                          onChange={(event) => setReplyBody(event.target.value)}
                          placeholder={t('supportTickets.detail.replyPlaceholder')}
                          rows={5}
                          className="min-h-32"
                        />
                      )}
                    </FormField>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="support-ticket-internal"
                          checked={replyInternal}
                          onCheckedChange={(value) => setReplyInternal(value === true)}
                        />
                        <Label htmlFor="support-ticket-internal" className="text-body-sm font-normal">
                          {t('supportTickets.detail.internalNoteHint')}
                        </Label>
                      </div>
                      <Button
                        loading={postMessage.isPending}
                        disabled={replyBody.trim().length < 1}
                        onClick={() =>
                          postMessage.mutate({
                            body: replyBody.trim(),
                            isInternal: replyInternal,
                          })
                        }
                      >
                        {replyInternal
                          ? t('supportTickets.detail.sendInternal')
                          : t('supportTickets.detail.sendReply')}
                      </Button>
                    </div>
                  </div>
                ) : null}
              </>
            )}
          </DialogBody>

          {ticket ? (
            <DialogFooter>
              {canStartVisit && !currentSession ? (
                <Button
                  variant="outline"
                  onClick={() => {
                    setVisitReason(defaultVisitReason);
                    setVisitOpen(true);
                  }}
                >
                  {t('supportTickets.detail.startVisit')}
                </Button>
              ) : null}
              {canEscalate ? (
                <Button variant="outline" onClick={() => setEscalateOpen(true)}>
                  {t('supportTickets.detail.escalate')}
                </Button>
              ) : null}
              {canReopen ? (
                <Button variant="outline" loading={reopen.isPending} onClick={() => reopen.mutate()}>
                  {t('supportTickets.detail.reopen')}
                </Button>
              ) : null}
              {canResolve ? (
                <Button variant="outline" loading={resolve.isPending} onClick={() => resolve.mutate()}>
                  {t('supportTickets.detail.resolve')}
                </Button>
              ) : null}
              {canClose ? (
                <Button variant="destructive" loading={close.isPending} onClick={() => close.mutate()}>
                  {t('supportTickets.detail.close')}
                </Button>
              ) : null}
            </DialogFooter>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog
        open={escalateOpen}
        onOpenChange={(next) => {
          if (!next) {
            setEscalateOpen(false);
            setEscalateReason('');
          }
        }}
      >
        <DialogContent closeLabel={t('common.cancel')} size="sm">
          <DialogHeader>
            <DialogTitle>{t('supportTickets.detail.escalateTitle')}</DialogTitle>
            <DialogDescription>{t('supportTickets.detail.escalateDescription')}</DialogDescription>
          </DialogHeader>
          <DialogBody className="overflow-visible" contentClassName="flex flex-col gap-4">
            <FormField label={t('supportTickets.detail.escalateReason')} hint={t('supportTickets.detail.optional')}>
              {(field) => (
                <Textarea
                  {...field}
                  value={escalateReason}
                  onChange={(event) => setEscalateReason(event.target.value)}
                  placeholder={t('supportTickets.detail.escalateReasonPlaceholder')}
                />
              )}
            </FormField>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEscalateOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              loading={escalate.isPending}
              onClick={() => escalate.mutate(escalateReason)}
            >
              {t('supportTickets.detail.escalateConfirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={visitOpen}
        onOpenChange={(next) => {
          if (!next) {
            setVisitOpen(false);
            setVisitReason('');
            setVisitLink(null);
          }
        }}
      >
        <DialogContent closeLabel={t('common.cancel')} size="sm">
          <DialogHeader>
            <DialogTitle>{t('supportTickets.detail.startVisitTitle')}</DialogTitle>
            <DialogDescription>
              {visitLink
                ? t('churches.supportVisitLinkHint')
                : t('supportTickets.detail.startVisitDescription')}
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="overflow-visible" contentClassName="flex flex-col gap-4">
            {visitLink ? (
              <p className="break-all text-body-sm text-fg-secondary">{visitLink}</p>
            ) : (
              <FormField label={t('churches.reason')} required>
                {(field) => (
                  <Textarea
                    {...field}
                    value={visitReason}
                    onChange={(event) => setVisitReason(event.target.value)}
                    placeholder={t('churches.reasonPlaceholder')}
                  />
                )}
              </FormField>
            )}
          </DialogBody>
          <DialogFooter>
            {visitLink ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => {
                    void navigator.clipboard.writeText(visitLink).then(
                      () => toast.success(t('churches.supportVisitCopied')),
                      () => toast.error(t('churches.supportVisitCopyFailed')),
                    );
                  }}
                >
                  {t('churches.supportVisitCopy')}
                </Button>
                <Button onClick={() => window.open(visitLink, '_blank', 'noopener')}>
                  {t('churches.supportVisitOpen')}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setVisitOpen(false)}>
                  {t('common.cancel')}
                </Button>
                <Button
                  loading={startVisit.isPending}
                  disabled={visitReason.trim().length < 3}
                  onClick={() => startVisit.mutate(visitReason.trim())}
                >
                  {t('churches.supportVisitConfirm')}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
